/**
 * WantedPromosPage.jsx — v1.0.0 Gate 4 Item 2
 *
 * Combined list: existing wishlist (linked businesses) + wanted_promos
 * (free-text or linked via survey / manual add). Each entry shows an
 * offer status chip — "Has Birthday Offer" or "No Birthday Offer Yet".
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft, Gift, Plus, Trash2, Loader2, Bookmark, ChevronRight,
} from "lucide-react";
import { API, axios } from "@/api";
import { useTheme } from "@/contexts/ThemeContext";
import { BrandLogo } from "@/components/shared/BrandLogo";
import { BottomNav } from "@/components/shared/Navigation";
import { trackWantedPromoAdded } from "@/lib/events";

export default function WantedPromosPage() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState("");

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/wanted-promos`);
      setItems(res.data?.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleAdd = async () => {
    const name = addName.trim();
    if (!name) return;
    setAdding(true);
    try {
      const res = await axios.post(`${API}/wanted-promos`, { name, source: "manual" });
      if (res.data?.status === "added") {
        trackWantedPromoAdded(null, null, "wanted_promos_page");
        toast.success("Added to Wanted Promos");
      } else if (res.data?.status === "exists") {
        toast("Already in your list");
      }
      setAddName("");
      setAddOpen(false);
      fetchItems();
    } catch {
      toast.error("Couldn't add");
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (entryId) => {
    try {
      await axios.delete(`${API}/wanted-promos/${entryId}`);
      setItems((prev) => prev.filter((i) => i.entry_id !== entryId));
    } catch {
      toast.error("Couldn't remove");
    }
  };

  return (
    <div className="min-h-screen pb-24" style={{ background: theme.pageBg }} data-testid="wanted-promos-page">
      {/* Header */}
      <div className="px-5 pt-6 pb-4" style={{ background: theme.headerGradient }}>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-1.5 rounded-xl bg-white/10 hover:bg-white/20"
            data-testid="wanted-promos-back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-white" data-testid="wanted-promos-title">
              Wanted Promos
            </h1>
            <p className="text-white/70 text-xs mt-0.5">
              Businesses you'd like a birthday offer from
            </p>
          </div>
          <button
            onClick={() => setAddOpen(true)}
            className="w-9 h-9 rounded-xl bg-white/15 hover:bg-white/25 text-white flex items-center justify-center"
            data-testid="wanted-promos-add-btn"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin" style={{ color: theme.primary }} />
        </div>
      ) : items.length === 0 ? (
        <div className="flex items-center justify-center min-h-[50vh]" data-testid="wanted-promos-empty">
          <div className="text-center px-8">
            <Bookmark className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-base font-semibold text-slate-700 leading-relaxed">
              No wanted promos yet.<br />
              Tell us what you're hoping to see.
            </p>
            <button
              onClick={() => setAddOpen(true)}
              className="mt-6 px-6 py-3 rounded-full text-white text-sm font-semibold"
              style={{ backgroundColor: theme.buttonBg }}
              data-testid="wanted-promos-empty-add-btn"
            >
              Add a business
            </button>
          </div>
        </div>
      ) : (
        <div className="px-4 pt-4 space-y-2" data-testid="wanted-promos-list">
          {items.map((item, i) => (
            <WantedPromoRow
              key={item.entry_id || i}
              item={item}
              onOpen={() => item.business_id && navigate(`/offer/${item.business_id}`)}
              onRemove={() => handleRemove(item.entry_id)}
            />
          ))}
        </div>
      )}

      {/* Add sheet */}
      {addOpen && (
        <div className="fixed inset-0 z-[95] flex items-end" data-testid="wanted-promos-add-sheet">
          <div className="absolute inset-0 bg-black/40" onClick={() => setAddOpen(false)} />
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            className="relative w-full max-w-md mx-auto bg-white rounded-t-3xl p-5"
          >
            <h3 className="text-sm font-bold text-slate-800 mb-2">Add a business</h3>
            <p className="text-[11px] text-slate-500 mb-3">
              We'll match it to our database if we have it, or save it as a request.
            </p>
            <input
              type="text"
              value={addName}
              onChange={(e) => setAddName(e.target.value.slice(0, 80))}
              placeholder="e.g., Chipotle, local bakery…"
              className="w-full px-3 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-200"
              maxLength={80}
              data-testid="wanted-promos-name-input"
            />
            <div className="flex gap-2 mt-3">
              <button
                onClick={() => setAddOpen(false)}
                className="flex-1 py-3 rounded-xl bg-slate-100 text-slate-600 text-sm font-semibold"
                data-testid="wanted-promos-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleAdd}
                disabled={!addName.trim() || adding}
                className="flex-1 py-3 rounded-xl bg-sky-500 text-white text-sm font-semibold disabled:opacity-50"
                data-testid="wanted-promos-confirm-btn"
              >
                {adding ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Add"}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      <BottomNav />
    </div>
  );
}

function WantedPromoRow({ item, onOpen, onRemove }) {
  const hasOffer = item.offer_status === "has_offer";
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex items-center gap-3 p-3 bg-white rounded-2xl border border-slate-100 shadow-sm"
      data-testid={`wanted-promo-item-${item.entry_id}`}
    >
      {item.business_id ? (
        <BrandLogo name={item.name} logoUrl={item.logo_url} size="sm" />
      ) : (
        <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
          <Gift className="w-5 h-5 text-slate-400" />
        </div>
      )}
      <button
        onClick={onOpen}
        disabled={!item.business_id}
        className="flex-1 min-w-0 text-left"
        data-testid={`wanted-promo-open-${item.entry_id}`}
      >
        <p className="text-sm font-bold text-slate-800 truncate">{item.name}</p>
        <div className="flex items-center gap-1.5 mt-0.5">
          <span
            className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap ${
              hasOffer
                ? "bg-emerald-100 text-emerald-700"
                : "bg-slate-100 text-slate-500"
            }`}
            data-testid={`wanted-promo-status-${item.entry_id}`}
          >
            {hasOffer ? "Has Birthday Offer" : "No Birthday Offer Yet"}
          </span>
          {item.category && (
            <span className="text-[10px] text-slate-400 truncate">{item.category}</span>
          )}
        </div>
      </button>
      {item.business_id && (
        <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
      )}
      <button
        onClick={onRemove}
        className="p-1.5 rounded-lg text-slate-400 hover:text-rose-500 hover:bg-rose-50 flex-shrink-0"
        data-testid={`wanted-promo-remove-${item.entry_id}`}
        aria-label="Remove"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </motion.div>
  );
}
